import React, {
  useCallback,
  useImperativeHandle,
  useState,
  useEffect,
} from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import _ from "lodash";
import { Modal, Button, RadioButton } from "@toddle-edu/ds-web";
import { useI18n } from "Hooks";
import classes from "./RecurrenceConfirmationModal.scss";
import { Trans } from "react-i18next";

const styles = {
  modalBody: { paddingBottom: "0px" },
};

const REPEAT_SCOPE_OPTIONS = {
  THIS_EVENT_ONLY: "THIS",
  THIS_AND_ALL_FUTURE: "THIS_FOLLOWING",
  ALL_EVENTS: "ALL",
};

const RecurrenceConfirmationModal = ({
  isOpen: isOpenProp = false,
  onClose = _.noop,
  onSave = _.noop,
  actionRef,
  updatedLabelText,
  showRepeatInfo = false,
  title,
  showDeleteInfo = false,
  isRemoveRepeatRecurranceSettings = false,
  isDateChanged,
  isRecurringEventSettingsUpdated,
  testId = "recurrence-confirmation-modal",
  thisLabelText = "calendar:edit_this_event_only",
  thisAndAllFututreLabelText = "calendar:edit_this_and_all_future_events",
  allEventsLabelText = "calendar:edit_all_events",
}) => {
  const { t } = useI18n(["calendar", "common"]);

  const radioButtonOptions = _.compact([
    isRecurringEventSettingsUpdated
      ? null
      : {
          value: REPEAT_SCOPE_OPTIONS.THIS_EVENT_ONLY,
          label: t(thisLabelText),
        },
    {
      value: REPEAT_SCOPE_OPTIONS.THIS_AND_ALL_FUTURE,
      label: t(thisAndAllFututreLabelText),
    },
    isDateChanged
      ? null
      : {
          value: REPEAT_SCOPE_OPTIONS.ALL_EVENTS,
          label: t(allEventsLabelText),
        },
  ]);

  const [isOpen, setIsOpen] = useState(isOpenProp);
  const [selectedScope, setSelectedScope] = useState(
    radioButtonOptions[0]?.value
  );

  const handleSave = useCallback(() => {
    onSave(selectedScope);
  }, [onSave, selectedScope]);

  const handleClose = useCallback(() => {
    setIsOpen(false);
    onClose();
  }, [onClose]);

  useEffect(() => {
    setSelectedScope(radioButtonOptions[0]?.value);
  }, [isRecurringEventSettingsUpdated, isDateChanged]);

  useImperativeHandle(actionRef, () => ({
    close: () => setIsOpen(false),
    open: () => setIsOpen(true),
    save: handleSave,
    cancel: handleClose,
  }));

  const footerClass = classNames(classes.footer);

  return (
    <Modal
      height="fit-content"
      width="480px"
      isOpen={isOpen}
      onClose={handleClose}
    >
      <Modal.Header title={title} isClosable={true} />

      <Modal.Body style={styles.modalBody}>
        {isRemoveRepeatRecurranceSettings ? (
          <div className={classNames("text-body", classes.confirmRemove)}>
            <Trans i18nKey="calendar:confirm_remove_recurring_event">
              <b>delete all the events in this series</b>
            </Trans>
          </div>
        ) : (
          _.map(radioButtonOptions, option => {
            const isSelected = selectedScope === option.value;
            const handleScopeChange = () => {
              setSelectedScope(option?.value);
            };

            return (
              <div key={option.value} className={classes.radioButtonWrapper}>
                <RadioButton
                  name="repeatScope"
                  value={option.value}
                  checked={isSelected}
                  onChange={handleScopeChange}
                  dsVersion="2.0"
                  size="medium"
                  label={option.label}
                  testId={`${testId}-${_.get(option, "value")}`}
                >
                  <span className={classes.radioButtonDescription}>
                    {option.label}
                  </span>
                </RadioButton>
              </div>
            );
          })
        )}
        {showDeleteInfo || showRepeatInfo ? (
          <div>
            <div className={classes.divider} />
            {showDeleteInfo ? (
              <div className={classNames(classes.repeatLabel, "text-label")}>
                {t("calendar:removed_from_calendar")}
              </div>
            ) : null}
            {showRepeatInfo ? (
              <div className={classes.repeatWrapper}>
                <div className={classNames(classes.repeatLabel, "text-label")}>
                  {t("calendar:repeat")}
                </div>
                <div>
                  <div className={classNames(classes.repeatValue, "text-body")}>
                    {updatedLabelText}
                  </div>
                </div>
              </div>
            ) : null}
          </div>
        ) : null}
      </Modal.Body>

      <Modal.Footer>
        <div className={footerClass}>
          <Button
            variant="neutral"
            type="outlined"
            onClick={handleClose}
            className={classes.cancelButton}
            testId={`${testId}-cancel`}
          >
            {t("common:cancel")}
          </Button>
          <Button
            variant="progressive"
            onClick={handleSave}
            disabled={_.isNil(selectedScope)}
            className={classes.saveButton}
            testId={`${testId}-continue`}
          >
            {t("common:continue")}
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

RecurrenceConfirmationModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  selectedScope: PropTypes.oneOf(_.values(REPEAT_SCOPE_OPTIONS)),
  onScopeChange: PropTypes.func.isRequired,
  eventTitle: PropTypes.string,
};

RecurrenceConfirmationModal.defaultProps = {
  selectedScope: null,
  eventTitle: "",
};

export default RecurrenceConfirmationModal;
export { REPEAT_SCOPE_OPTIONS };
